import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchCombobox } from "@/components/ui/search-combobox";
import { searchHardwareByField } from "@/lib/snipeit/hardware";
import type { SnipeItAsset } from "@/lib/snipeit/types";
import { translateStatus } from "@/common/helpers/status";

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-xs font-medium">{label}</span>
      <span className="text-foreground text-sm">{value}</span>
    </div>
  );
}

interface SelectDeviceCardProps {
  queryKeyPrefix: string;
  onSelect: (asset: SnipeItAsset | null) => void;
  filterFn?: (asset: SnipeItAsset) => boolean;
  /** Passed server-side as location_id to narrow search results */
  locationId?: number;
  /** Passed server-side as status_type (e.g. "deployable") */
  statusType?: string;
}

export function SelectDeviceCard({
  queryKeyPrefix,
  onSelect,
  filterFn,
  locationId,
  statusType,
}: SelectDeviceCardProps) {
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState<SnipeItAsset | null>(null);

  function handleSelect(asset: SnipeItAsset | null) {
    if (!asset) {
      setSelected(null);
      onSelect(null);
      return;
    }
    setSelected(asset);
    setSearchInput(asset.asset_tag ?? asset.serial ?? "");
    onSelect(asset);
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (selected && value !== selected.asset_tag && value !== selected.serial) {
      setSelected(null);
      onSelect(null);
    }
  }

  return (
    <>
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-muted-foreground text-sm font-medium">Gerät suchen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <label className="text-muted-foreground text-xs font-medium">Asset Tag & Seriennummer</label>
            <SearchCombobox
              placeholder="Asset Tag oder Seriennummer suchen…"
              queryKey={`${queryKeyPrefix}-search`}
              searchFn={(query) =>
                searchHardwareByField(query, ["asset_tag", "serial"], { locationId, statusType }).then((results) =>
                  results
                    .filter((asset) => filterFn?.(asset) ?? true)
                    .map((asset) => ({
                      id: asset.id,
                      label: asset.asset_tag
                        ? `${asset.asset_tag} (${asset.serial || "Keine SN"})`
                        : asset.serial || "Unbekannt",
                      sublabel: asset.name,
                      data: asset,
                    })),
                )
              }
              value={searchInput}
              onInputChange={handleSearchChange}
              onSelect={(result) => {
                handleSelect((result.data as SnipeItAsset) ?? null);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {selected && (
        <Card className="border-border shadow-sm">
          <CardContent className="max-h-[40dvh] space-y-3 overflow-y-auto pt-4 md:max-h-[calc(100vh_-_340px)]">
            <InfoRow label="Name" value={selected.name} />
            <InfoRow label="Modell" value={selected.model?.name} />
            <InfoRow label="Seriennummer" value={selected.serial} />
            <InfoRow
              label="Status"
              value={
                selected.status_label
                  ? translateStatus(selected.status_label.name, selected.status_label.status_meta)
                  : null
              }
            />
            <InfoRow label="Aktueller Standort" value={selected.location?.name} />
            <InfoRow label="Herausgegeben an" value={selected.assigned_to?.name} />
            <InfoRow label="Notizen" value={selected.notes} />
          </CardContent>
        </Card>
      )}
    </>
  );
}
